import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
    const { login, isAuthenticated } = useAuth();
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [csrfToken, setCsrfToken] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [blocked, setBlocked] = useState(false);

    useEffect(() => {
        // Generate CSRF token
        const token = generateCSRFToken();
        setCsrfToken(token);

        // Check if user is blocked due to too many attempts
        /*
        const blockedUntil = localStorage.getItem('admin_blocked_until');
        if (blockedUntil && new Date().getTime() < parseInt(blockedUntil)) {
            setBlocked(true);
            setError('Zu viele Anmeldeversuche. Bitte warten Sie 15 Minuten.');
        }
        */

        // Add security headers via meta tags
        const securityMeta = document.createElement('meta');
        securityMeta.httpEquiv = 'Content-Security-Policy';
        securityMeta.content = "default-src 'self'; form-action 'self'";
        document.head.appendChild(securityMeta);

        return () => {
            if (document.head.contains(securityMeta)) {
                document.head.removeChild(securityMeta);
            }
        };
    }, []);

    const generateCSRFToken = () => {
        return btoa(Math.random().toString()).substr(10, 15);
    };

    if (isAuthenticated) {
        return <Navigate to="/admin" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (blocked) {
            setError('Anmeldung gesperrt. Bitte warten Sie.');
            return;
        }

        if (attempts >= 3) {
            setBlocked(true);
            localStorage.setItem('admin_blocked_until', (new Date().getTime() + 15 * 60 * 1000).toString());
            setError('Zu viele fehlgeschlagene Versuche. Zugang für 15 Minuten gesperrt.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Validate CSRF token
            if (!csrfToken) {
                throw new Error('Sicherheitstoken fehlt');
            }

            const result = await login({
                ...credentials,
                csrfToken: csrfToken,
                timestamp: new Date().getTime()
            });

            if (!result.success) {
                setAttempts(prev => prev + 1);
                setError(result.error || 'Anmeldung fehlgeschlagen');
                // Generate new CSRF token after failed attempt
                setCsrfToken(generateCSRFToken());
            } else {
                // Reset attempts on successful login
                setAttempts(0);
                localStorage.removeItem('admin_blocked_until');
            }
        } catch (error) {
            setAttempts(prev => prev + 1);
            setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            {/* Security Notice */}
            <div className="absolute top-4 left-4 right-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-blue-700">
                            Sicherer Administratorbereich - Nur für autorisierte Benutzer
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
                        <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Sunsite Admin-Bereich
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Sichere Anmeldung für Administratoren
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
                    <input type="hidden" name="csrfToken" value={csrfToken} />
                    <input type="hidden" name="honeypot" value="" tabIndex="-1" />

                    <div className="rounded-md shadow-sm -space-y-px">
                        <input
                            name="username"
                            type="text"
                            required
                            value={credentials.username}
                            onChange={handleChange}
                            disabled={blocked || loading}
                            autoComplete="off"
                            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                            placeholder="Benutzername"
                            maxLength="50"
                        />
                        <input
                            name="password"
                            type="password"
                            required
                            value={credentials.password}
                            onChange={handleChange}
                            disabled={blocked || loading}
                            autoComplete="new-password"
                            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                            placeholder="Passwort"
                            maxLength="100"
                        />
                    </div>

                    {(error || blocked) && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            <div className="flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                            {attempts > 0 && attempts < 3 && (
                                <p className="mt-1 text-xs">
                                    Verbleibende Versuche: {3 - attempts}
                                </p>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || blocked}
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                            <svg className="h-5 w-5 text-primary-500 group-hover:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                        </span>
                        {loading ? 'Wird angemeldet...' : blocked ? 'Gesperrt' : 'Sicher anmelden'}
                    </button>

                    <div className="text-xs text-gray-500 text-center">
                        <p>Diese Seite ist durch erweiterte Sicherheitsmaßnahmen geschützt.</p>
                        <p>Alle Anmeldeversuche werden protokolliert.</p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;
